<template>
  <div class="filter-form">
    <GridContainer>
      <div class="filter-form__filter-wrapper">
        <!-- Filters -->
        <div class="filter-form__filter-container">
          <span class="filter-form__label">Filtern nach:</span>

          <!-- Product Categories -->
          <ProductCategoryDropdown
            v-if="!hiddenFilters.includes('productCategories')"
            v-model="fields.filter.productCategoryPaths"
            class="filter-form__entry"
            :aggregations="productCategoryAggregations"
            :item-url="itemUrls.productCategories"
            :disabled="isDisplexFullcoverOrPrivacy && hasRedundantFilterOptions('categories')"
            blank-text="Alle Kategorien"
            :tree="crossCategory"
            @change="submit"
          />

          <!-- Brands -->
          <ListDropdown
            v-if="!hiddenFilters.includes('brands')"
            v-model="fields.filter.brandSlugs"
            placeholder="Marke"
            multiple
            :options="options.brands"
            :item-url="itemUrls.brands"
            class="filter-form__entry"
            include-blank="Alle Marken"
            @change="submit"
          />

          <!-- Colors -->
          <ListDropdown
            v-if="!hiddenFilters.includes('colors')"
            v-model="fields.filter.colorSlugs"
            placeholder="Farben"
            multiple
            :options="options.colors"
            :item-url="itemUrls.colors"
            :disabled="listDropdownDisabled('colors')"
            include-blank="Alle Farben"
            class="filter-form__entry"
            @change="submit"
          >
            <template #beforeLabel="{ option }">
              <span
                class="filter-form__entry-color-preview"
                :style="{ backgroundColor: colorCodesBySlugs[option.value] }"
              />
            </template>
          </ListDropdown>

          <!-- Compatible Product Families -->
          <ListDropdown
            v-if="
              !$config.public.performanceMode.enabled &&
              !hiddenFilters.includes('compatibleProductFamilies')
            "
            v-model="fields.filter.compatibleProductFamilySlugs"
            placeholder="Kompatibilität"
            multiple
            :options="options.compatibleProductFamilies"
            :item-url="itemUrls.compatibleProductFamilies"
            :disabled="!options.compatibleProductFamilies.length"
            group-entries
            has-search
            include-blank="Egal"
            class="filter-form__entry"
            @change="submit"
          />

          <!-- Prices -->
          <PriceFilterDropdown
            :discounted="fields.filter.discounted"
            :discounted-count="discountedOffersCount"
            :onetime-price="fields.filter.onetimePrice"
            :disabled="priceFilterDisabled"
            :allow-discounts="!hiddenFilters.includes('discounts')"
            class="filter-form__entry"
            @change="updatePrices"
          />
        </div>

        <!-- Sort -->
        <div
          v-if="!hideSortDropdown"
          class="filter-form__sort-container"
        >
          <span class="filter-form__label grid__item">Sortieren nach:</span>
          <SortDropdown
            :value="variables.sort"
            :disabled="!hasOffers"
            :search-context="inSearchContext"
            class="filter-form__entry-sort"
            @change="updateSort"
          />
        </div>
        <div class="filter-form__entry-wide-fit filter-form__toggle-container">
          <!-- filter provider -->
          <template v-if="!hiddenFilters.includes('providers')">
            <Toggle
              v-for="provider in providers"
              :key="provider.slug"
              :value="providerToggleValue[provider.slug]"
              :label="providerLabel(provider.name)"
              :disabled="providerFilterDisabled"
              class="grid__item"
              @input="toggleProvider(provider.slug, $event)"
            />
          </template>

          <!-- wlan filter -->
          <div
            v-if="!hiddenFilters.includes('wlan') && wlanTag"
            class="filter-form__entry-wide-fit filter-form__wlan-toggle"
          >
            <Toggle
              :key="wlanTag?.tag?.slug"
              :value="isWlanSelected"
              class="grid__item"
              @input="toggleWlan($event)"
            >
              <SvgIcon
                :image="wlanIcon"
                class="filter-form__wlan-toggle-icon"
              />
              WLAN-Anbindung
            </Toggle>
          </div>

          <div
            v-if="
              !(hiddenFilters.includes('mza') && hiddenFilters.includes('msh')) &&
              (mzaCompatibleTag || mshCompatibleTag)
            "
            class="filter-form__app-toggle-wrapper filter-form__entry-wide-fit"
          >
            <!-- mza filter -->
            <span class="filter-form__app-toggle-label">Kompatibel mit</span>
            <div
              v-if="!hiddenFilters.includes('mza') && mzaCompatibleTag"
              class="filter-form__entry-wide-fit filter-form__app-toggle"
            >
              <Toggle
                :key="mzaCompatibleTag?.tag?.slug"
                :value="isMzaSelected"
                class="grid__item"
                title="MagentaZuhause App"
                @input="toggleMza($event)"
              >
                <SvgIcon
                  :image="mzaIcon"
                  class="filter-form__app-toggle-icon"
                />

                MagentaZuhause App
              </Toggle>
            </div>

            <!-- msh filter -->
            <div
              v-if="!hiddenFilters.includes('msh') && mshCompatibleTag"
              class="filter-form__entry-wide-fit filter-form__app-toggle"
            >
              <Toggle
                :key="mshCompatibleTag?.tag?.slug"
                :value="isMshSelected"
                class="grid__item"
                title="Magenta SmartHome App"
                @input="toggleMsh($event)"
              >
                <SvgIcon
                  :image="mshIcon"
                  class="filter-form__app-toggle-icon"
                />

                Magenta SmartHome App
              </Toggle>
            </div>
          </div>

          <slot name="additionalToggle" />
        </div>

        <slot name="additionalFilters" />
      </div>

      <!-- Selected Filters -->
      <ActiveFilters
        label="Deine Filter"
        :value="activeFilters"
        :has-filter="hasUserFilter"
        clear-text="Alle Filter löschen"
        class="filter-form__active"
        @remove="removeFilter"
        @clear="clearFilters"
      />
    </GridContainer>
  </div>
</template>

<script lang="ts">
import { cloneDeep, isEmpty, isUndefined, without } from 'lodash-es';
import { type PropType, defineComponent } from 'vue';

import { GridContainer, Toggle, ListDropdown } from '@i22-td-smarthome/component-library';
import ActiveFilters from '@/components/filter/active-filters.vue';
import mergeFilterVariables from '@/lib/filter-params/merge-variables';
import PriceFilterDropdown from '@/components/price-filter-dropdown.vue';
import ProductCategoryDropdown from '@/components/product-category-dropdown.vue';
import SortDropdown from '@/components/sort-dropdown.vue';
import traverseAggregations from '@/lib/aggregation-utils/traverse';
import type {
  OfferColorAggregation,
  Maybe,
  OfferProductCategoryAggregation,
  ProductCategory,
  PriceFilterParams,
  ProductProviderKey,
} from '@/lib/goliath/goliath.types';

import type {
  ItemUrls,
  Provider,
  FilterOptions,
  FilterParam,
  SelectedTag,
  AggregationTypes,
  FilterQuery,
  AggregationBucket,
  SelectedOfferAggregations,
} from './filter-types';

import wlanIcon from '@/assets/images/connection-wlan.svg';
import mzaIcon from '@/assets/images/mza-logo-magenta-solid.svg';
import mshIcon from '@/assets/images/msh-logo.svg';

const SHIPPING_PROVIDERS: Provider[] = [
  { slug: 'TELEKOM' as ProductProviderKey.Telekom, name: 'Telekom Deutschland GmbH' },
  { slug: 'BRODOS' as ProductProviderKey.Brodos, name: 'Brodos AG' },
];
const PROVIDER_LABEL_PREFIX = 'Versand und Verkauf durch';

const FIELDS: FilterQuery = {
  filter: {
    productCategoryPaths: [],
    brandSlugs: [],
    colorSlugs: [],
    compatibleProductFamilySlugs: [],
    discounted: undefined,
    onetimePrice: undefined,
    productProviders: [],
    productTagSlugs: [],
    anyInGroupOfProductTagSlugs: [],
  },
  pagination: undefined,
  sort: undefined,
  search: undefined,
};

const productCategories: AggregationBucket['productCategories'] = {
  source: 'productCategory',
  key: 'productCategoryPaths',
  getLabel({ name }) {
    return name;
  },
  getValue({ path }) {
    return path;
  },
};
const brands: AggregationBucket['brands'] = {
  source: 'brand',
  key: 'brandSlugs',
  getLabel({ name }) {
    return name;
  },
  getValue({ slug }) {
    return slug;
  },
};
const colors: AggregationBucket['colors'] = {
  source: 'color',
  key: 'colorSlugs',
  getLabel({ name }) {
    return name;
  },
  getValue({ slug }) {
    return slug;
  },
};
const compatibleProductFamilies: AggregationBucket['compatibleProductFamilies'] = {
  source: 'productFamily',
  key: 'compatibleProductFamilySlugs',
  getLabel({ model }) {
    return model;
  },
  getValue({ slug }) {
    return slug;
  },
};
const productTags: AggregationBucket['productTags'] = {
  source: 'tag',
  key: 'productTagSlugs',
  getLabel({ name }) {
    return name;
  },
  getValue({ slug }) {
    return slug;
  },
};

const AGGREGATION_BUCKETS = {
  productCategories,
  brands,
  colors,
  compatibleProductFamilies,
  productTags,
};

// Gets option values that are available for an aggregation with a particular
// name. Note that only bucket aggregations may have option values.
function getOptionsFromBucket(aggregations: SelectedOfferAggregations, name: AggregationTypes) {
  if (!aggregations) return [];
  const currentAggregation = aggregations[name];
  if (!currentAggregation || !Array.isArray(currentAggregation)) return [];
  const definition = AGGREGATION_BUCKETS[name];
  const options: FilterOptions[] = [];
  currentAggregation.forEach((aggregation) => {
    // @ts-ignore
    const source = aggregation[definition.source];
    const label = definition.getLabel(source) || '';
    const value = definition.getValue ? definition.getValue(source) : source;
    options.push({
      groupName: source.brand?.name,
      label,
      value,
      count: aggregation.offersCount || 0,
    });
  });
  return options;
}

export default defineComponent({
  name: 'FilterForm',
  components: {
    GridContainer,
    ActiveFilters,
    ListDropdown,
    PriceFilterDropdown,
    ProductCategoryDropdown,
    SortDropdown,
    Toggle,
  },
  props: {
    variables: {
      type: Object as PropType<FilterQuery>,
      required: true,
    },
    aggregations: {
      type: Object as PropType<SelectedOfferAggregations>,
      required: true,
    },
    itemUrls: {
      type: Object as PropType<ItemUrls>,
      default: () => {},
    },
    crossCategory: {
      type: Boolean,
      default: false,
    },
    hiddenFilters: {
      type: Array,
      default: () => [],
    },
    currentOffersCount: {
      type: Number,
      default: null,
    },
    hideSortDropdown: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit'],
  data() {
    return {
      fields: mergeFilterVariables(FIELDS, this.variables) as FilterQuery,
      providers: SHIPPING_PROVIDERS,
      providerLabelPrefix: PROVIDER_LABEL_PREFIX,
      wlanIcon,
      mzaIcon,
      mshIcon,
    };
  },
  computed: {
    hasUserFilter() {
      const { filter, search, complexSearch } = this.fields;
      if (search) return true;
      return (
        (filter.brandSlugs && filter.brandSlugs.length > 0) ||
        (filter.colorSlugs && filter.colorSlugs?.length > 0) ||
        (filter.compatibleProductFamilySlugs && filter.compatibleProductFamilySlugs?.length > 0) ||
        filter.discounted === true ||
        !!(filter.onetimePrice?.min || filter.onetimePrice?.max) ||
        (filter.productTagSlugs && filter.productTagSlugs.length > 0) ||
        (filter.anyInGroupOfProductTagSlugs && filter.anyInGroupOfProductTagSlugs.length > 0) ||
        (filter.productProviders && filter.productProviders?.length > 0) ||
        !!complexSearch
      );
    },
    hasOffers() {
      return this.currentOffersCount > 0;
    },
    providerLabel() {
      return (providerName: string) => `${this.providerLabelPrefix} ${providerName}`;
    },
    listDropdownDisabled() {
      return (listType: AggregationTypes) =>
        !this.options[listType].length ||
        (this.isDisplexFullcoverOrPrivacy && this.hasRedundantFilterOptions(listType));
    },
    providerToggleValue() {
      return this.providers.reduce((acc: Record<string, boolean>, { slug }) => {
        acc[slug] = this.fields.filter.productProviders?.includes(slug) || false;
        return acc;
      }, {});
    },
    options() {
      const options: Record<AggregationTypes, FilterOptions[]> = {
        productCategories: getOptionsFromBucket(this.aggregations, 'productCategories'),
        brands: getOptionsFromBucket(this.aggregations, 'brands'),
        colors: getOptionsFromBucket(this.aggregations, 'colors'),
        compatibleProductFamilies: getOptionsFromBucket(
          this.aggregations,
          'compatibleProductFamilies'
        ),
        productTags: getOptionsFromBucket(this.aggregations, 'productTags'),
      };
      return options;
    },
    colorCodesBySlugs(): Record<string, string> {
      const results: Record<string, string> = {};
      if (!this.aggregations.colors) return {};
      this.aggregations.colors.forEach((entry: Maybe<OfferColorAggregation>) => {
        if (!entry?.color) return;
        const { color } = entry;
        results[color.slug || ''] = color.code;
      });
      return results;
    },
    productCategoryAggregations(): OfferProductCategoryAggregation[] {
      return this.aggregations.productCategories || [];
    },
    rootProductCategory() {
      const agg = this.productCategoryAggregations.find((aggregations) => aggregations.selected);
      if (!agg) return undefined;
      return agg.productCategory as ProductCategory;
    },
    selectedProductCategories(): ProductCategory[] {
      const aggs = traverseAggregations(this.productCategoryAggregations, {
        filter: (aggregation: OfferProductCategoryAggregation, depth: number) =>
          aggregation.selected && depth === 1,
      });
      return aggs.map((agg) => agg.productCategory);
    },
    activeFilters() {
      const { filter } = this.fields;
      // Categories
      let tags: SelectedTag[] = this.selectedProductCategories.map((category) => ({
        label: category.name as string,
        value: { key: 'productCategoryPaths', value: category.path as string },
      }));

      // Brands, Colors, Compatibilities
      Object.keys(AGGREGATION_BUCKETS).forEach((name) => {
        // We need the category only, when we have cross-category filter
        if (!this.crossCategory && name === 'productCategories') return;
        const { key } = AGGREGATION_BUCKETS[name as AggregationTypes];
        const values = filter[key];
        if (!values) return;
        const options = this.options[name as AggregationTypes];
        const bucketActiveFilters = Array.from(values)
          .map((value) => {
            if (this.hiddenFilters.includes(name)) return undefined;
            const option = options?.find((o) => o.value === value);
            if (!option) return undefined;
            let { label } = option;
            if (!label) return undefined;
            if (name === 'compatibleProductFamilies') {
              label = `Kompatibel mit ${label}`;
            }
            if (value === 'i22-wifi-compatible') {
              label = 'WLAN-Anbindung';
            }
            return { label, value: { key, value } };
          })
          .filter((item) => item !== undefined)
          .sort((a, b) => a.label.localeCompare(b.label));

        tags = tags.concat(bucketActiveFilters);
      });

      // Discount, Prices
      if (filter.discounted) {
        tags.push({
          label: 'Rabattierte Angebote',
          value: { key: 'discounted', value: true },
        });
      }

      if (this.priceFilterLabel && filter.onetimePrice) {
        tags.push({
          label: this.priceFilterLabel,
          value: {
            key: 'onetimePrice',
            value: filter.onetimePrice,
          },
        });
      }

      if (filter.productProviders && !this.hiddenFilters.includes('providers')) {
        filter.productProviders.forEach((provider) => {
          const mappedProvider =
            this.providers.find(({ slug }) => slug === provider)?.name || provider;
          tags.push({
            label: `${this.providerLabelPrefix} ${mappedProvider}`,
            value: { key: 'productProviders', value: provider },
          });
        });
      }

      if (
        filter.anyInGroupOfProductTagSlugs &&
        !this.hiddenFilters.includes('mza') &&
        !this.hiddenFilters.includes('msh')
      ) {
        filter.anyInGroupOfProductTagSlugs.forEach((group) => {
          if (group.some((tag) => tag.startsWith('mza'))) {
            tags.push({
              label: `Kompatibel mit MagentaZuhause App`,
              value: { key: 'anyInGroupOfProductTagSlugs', value: 'mza' },
            });
          }
          if (group.some((tag) => tag.startsWith('msh'))) {
            tags.push({
              label: `Kompatibel mit Magenta SmartHome App`,
              value: { key: 'anyInGroupOfProductTagSlugs', value: 'msh' },
            });
          }
        });
      }
      return tags;
    },
    priceFilterDisabled() {
      const { filter } = this.fields;
      return !this.hasOffers && !filter.onetimePrice && !filter.discounted;
    },
    priceFilterLabel() {
      const filter = this.fields.filter.onetimePrice;
      if (!filter || isEmpty(filter)) return undefined;
      if (filter.max === 0) {
        return 'Kostenlos';
      }
      if (!isUndefined(filter.min) && !isUndefined(filter.max)) {
        if (filter.min === filter.max) {
          return `Preis von ${filter.min} €`;
        }
        return `Preise von ${filter.min} € bis ${filter.max} €`;
      }
      if (!isUndefined(filter.min)) {
        return `Preise ab ${filter.min} €`;
      }
      if (!isUndefined(filter.max)) {
        return `Preise bis ${filter.max} €`;
      }
      return undefined;
    },
    discountedOffersCount() {
      if (!this.aggregations) return undefined;
      const discountsAggregation = this.aggregations.discounts;
      if (!discountsAggregation?.discounted) return undefined;
      return discountsAggregation.discounted.offersCount as number;
    },
    inSearchContext() {
      return !!this.fields.search;
    },
    providerFilterDisabled() {
      return false;
    },
    isDisplexFullcoverOrPrivacy() {
      return this.activeFilters.some(
        ({ value: { value } }) => value === 'privacy' || value === 'fullcover'
      );
    },
    wlanTag() {
      return this.aggregations?.productTags?.find(
        (item) => item.tag?.slug === 'i22-wifi-compatible'
      );
    },
    mzaCompatibleTag() {
      return this.aggregations?.productTags?.find(
        (item) => item.tag?.slug && ['mzabasic', 'mzapro'].includes(item.tag?.slug)
      );
    },
    mshCompatibleTag() {
      return this.aggregations?.productTags?.find(
        (item) => item.tag?.slug && ['mshfree', 'mshpro'].includes(item.tag?.slug)
      );
    },
    isWlanSelected() {
      if (!this.wlanTag?.tag?.slug) return false;
      return this.fields?.filter?.productTagSlugs?.includes(this.wlanTag.tag.slug);
    },
    isMzaSelected() {
      if (!this.mzaCompatibleTag?.tag?.slug) return false;
      return this.fields?.filter?.anyInGroupOfProductTagSlugs?.some((group) =>
        group.includes(this.mzaCompatibleTag?.tag?.slug as string)
      );
    },
    isMshSelected() {
      if (!this.mshCompatibleTag?.tag?.slug) return false;
      return this.fields?.filter?.anyInGroupOfProductTagSlugs?.some((group) =>
        group.includes(this.mshCompatibleTag?.tag?.slug as string)
      );
    },
  },
  watch: {
    variables(variables) {
      this.fields = mergeFilterVariables(FIELDS, variables);
    },
  },
  methods: {
    hasRedundantFilterOptions(type: AggregationTypes | 'categories') {
      // A redundant filter option links to the same amount of offers as currently shown
      if (type === 'colors' || type === 'brands') {
        const option = this.options[type];
        if (!Array.isArray(option)) return false;
        return option.every((item) => item.count === this.currentOffersCount);
      }
      if (type === 'categories') {
        return Array.isArray(this.productCategoryAggregations[0]?.children)
          ? this.productCategoryAggregations[0]?.children.every(
              (category) => category?.offersCount === this.currentOffersCount
            )
          : false;
      }
      return false;
    },
    updatePrices(prices: { discounted: boolean; onetimePrice: PriceFilterParams }) {
      if (prices.discounted) {
        this.fields.filter.discounted = prices.discounted;
      } else {
        delete this.fields.filter.discounted;
      }
      if (prices.onetimePrice) {
        this.fields.filter.onetimePrice = { ...prices.onetimePrice };
      } else {
        delete this.fields.filter.onetimePrice;
      }
      this.submit();
    },
    updateSort(sort: FilterQuery['sort']) {
      this.fields.sort = sort;
      this.submit();
    },
    toggleProvider(providerSlug: Provider['slug'], newValue: boolean) {
      const currentProviders = this.fields.filter.productProviders || [];

      this.fields.filter.productProviders = newValue
        ? [...currentProviders, providerSlug]
        : currentProviders.filter((slug) => slug !== providerSlug);

      this.submit();
    },
    toggleWlan(newValue: boolean) {
      const currentProductSlugs = this.fields.filter.productTagSlugs || [];

      if (this.wlanTag?.tag?.slug) {
        this.fields.filter.productTagSlugs = newValue
          ? [...currentProductSlugs, this.wlanTag?.tag?.slug]
          : currentProductSlugs.filter((slug) => slug !== this.wlanTag?.tag?.slug);

        this.submit();
      }
    },
    toggleMza(newValue: boolean) {
      const currentProductSlugGroups = this.fields.filter.anyInGroupOfProductTagSlugs || [];

      const mzaSlugs = ['mzabasic', 'mzapro'];

      if (this.mzaCompatibleTag?.tag?.slug) {
        this.fields.filter.anyInGroupOfProductTagSlugs = newValue
          ? [...currentProductSlugGroups, mzaSlugs]
          : currentProductSlugGroups.filter(
              (slugGroup) => !slugGroup.some((slug) => mzaSlugs.includes(slug))
            );

        this.submit();
      }
    },
    toggleMsh(newValue: boolean) {
      const currentProductSlugGroups = this.fields.filter.anyInGroupOfProductTagSlugs || [];

      const mshSlugs = ['mshfree', 'mshpro'];

      if (this.mshCompatibleTag?.tag?.slug) {
        this.fields.filter.anyInGroupOfProductTagSlugs = newValue
          ? [...currentProductSlugGroups, mshSlugs]
          : currentProductSlugGroups.filter(
              (slugGroup) => !slugGroup.some((slug) => mshSlugs.includes(slug))
            );

        this.submit();
      }
    },

    removeFilter({ key, value }: SelectedTag['value']) {
      const { filter } = this.fields;
      switch (key) {
        case 'discounted':
          delete filter.discounted;
          break;
        case 'onetimePrice':
          delete filter.onetimePrice;
          break;
        case 'productProviders':
          filter.productProviders = filter.productProviders?.filter(
            (provider) => provider !== value
          );
          break;
        case 'productTagSlugs':
          filter.productTagSlugs = filter.productTagSlugs?.filter((slug) => slug !== value);
          break;
        case 'anyInGroupOfProductTagSlugs': {
          filter.anyInGroupOfProductTagSlugs = filter.anyInGroupOfProductTagSlugs?.filter(
            (group) => !group.some((item) => item.startsWith(value as string))
          );
          break;
        }
        default: {
          const values = filter[key] || [];
          let newValues = without(values, value);

          if (
            key === 'productCategoryPaths' &&
            newValues.length === 0 &&
            !this.crossCategory &&
            this.rootProductCategory?.path
          ) {
            // When removing all product category filters, slip in the the root
            // category as only category filter instead.
            newValues = [this.rootProductCategory?.path];
          }

          // @ts-ignore
          filter[key] = newValues;
        }
      }
      this.submit();
    },
    clearFilters() {
      const filter: FilterParam = {
        onetimePrice: undefined,
        discounted: undefined,
      };

      if (!this.crossCategory) {
        // If no specific product category filters exist, but there is a root category,
        // set the root category as the only category filter
        if (this.rootProductCategory?.path) {
          filter.productCategoryPaths = [this.rootProductCategory.path];
        } else if (
          this.fields.filter?.productCategoryPaths &&
          this.fields.filter.productCategoryPaths.length > 0
        ) {
          // if no root category is present, use any category. Otherwise, the user will be redirected to the start page.
          filter.productCategoryPaths = this.fields.filter.productCategoryPaths;
        }
      }

      this.fields.filter = filter;

      this.submit();
    },
    submit() {
      const fields = cloneDeep(this.fields);
      delete fields.pagination;
      this.$emit('submit', fields);
    },
  },
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.filter-form {
  position: relative;

  &__filter-wrapper {
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: 1fr;
    grid-template-areas: 'filters' 'toggles' 'sort';
    padding-top: $grid-base;
    @include mq($mq-md) {
      grid-template-columns: calc(100% / 6) * 5 16.66667%;
      grid-template-areas: 'filters sort' 'toggles toggles';
    }
  }

  &__label {
    display: inline-block;
    font-size: 15px;
    line-height: 18px;
    margin-bottom: 5px;
    width: 100%;
    user-select: none;
    padding-inline: $grid-base * 0.5;
  }

  &__filter-container,
  &__sort-container {
    display: flex;
    flex-flow: row wrap;
    width: 100%;
    align-content: flex-start;
    margin-bottom: $grid-base;
  }

  &__filter-container {
    grid-area: filters;
  }

  &__sort-container {
    grid-area: sort;
  }

  &__toggle-container {
    grid-area: toggles;
    @include mq(false, $mq-md - 1) {
      margin-bottom: $grid-base;
    }
  }

  &__entry {
    flex: 0 0 50%;
    max-width: 50%;
    overflow: visible;
    padding-inline: $grid-base * 0.5;
    margin-bottom: $grid-base;

    &-wide {
      flex: 0 0 100%;
      max-width: 100%;

      &-fit {
        max-width: fit-content;
      }
    }

    :deep(.dropdown-container) {
      margin-inline: $grid-base * 0.5;
    }

    @include mq($mq-md) {
      flex: 0 0 20%;
      max-width: 20%;
      margin-bottom: 0;
    }
  }

  &__entry-sort {
    padding-inline: $grid-base * 0.5;
    width: 100%;

    :deep(.dropdown-container) {
      margin-inline: $grid-base * 0.5;
    }
  }

  &__entry-color-preview {
    border-radius: 50%;
    display: block;
    height: 12px;
    width: 12px;
    border: 1px solid color(grey, 25000);
  }

  &__wlan-toggle {
    display: inline;

    &-icon {
      width: 17px;
      height: 17px;
      position: relative;
      bottom: 2px;
    }

    :deep(.toggle__label) {
      display: flex;
      align-items: flex-end;
      gap: 0.5 * $grid-base;
    }
  }

  &__app-toggle {
    display: inline;

    &-label {
      @include font-size(15px, 18px);
      margin-top: 0.75 * $grid-base;
      grid-row: 1 / 3;
    }

    &-wrapper {
      display: grid;
      grid-template-columns: 40% 60%;
      padding-inline: $grid-base;
      gap: $grid-base $grid-base * 0.5;

      @include mq($mq-md) {
        display: inline-flex;
        align-items: flex-start;
        flex-wrap: nowrap;
        vertical-align: top;
        gap: $grid-base;
      }
    }

    &-icon {
      width: 2 * $grid-base;
      height: 2 * $grid-base;
      flex: 0 0 2 * $grid-base;
      margin-bottom: 4px;
    }

    :deep(.toggle__label) {
      display: flex;
      align-items: center;
      gap: 0.5 * $grid-base;
    }

    :deep(.toggle) {
      padding-inline: 0;
    }
  }

  &__active {
    padding-inline: $grid-base;
  }
}
</style>
